var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isFocusable } from 'tabbable';
export { debounce } from 'debounce';
let scrollAnimationFrame = 0;
export const isScrollDisabled = () => {
    return document.body.classList.contains('is-scroll-disabled');
};
export const disableBodyScroll = () => {
    const contentElement = document.querySelector('#page-container');
    if (contentElement && !isScrollDisabled()) {
        const curScroll = window.scrollY || window.pageYOffset || document.body.scrollTop + (document.documentElement && document.documentElement.scrollTop || 0);
        document.body.classList.add('is-scroll-disabled');
        contentElement.style.top = -curScroll + 'px';
        window.scroll(0, 0);
    }
};
export const enableBodyScroll = () => {
    const contentElement = document.querySelector('#page-container');
    if (contentElement && isScrollDisabled()) {
        const bodyScroll = contentElement.style && contentElement.style.top ? parseInt(contentElement.style.top, 10) : undefined;
        document.body.classList.remove('is-scroll-disabled');
        if (bodyScroll !== undefined) {
            window.scroll(0, -bodyScroll);
            contentElement.style.top = '';
        }
    }
};
export const getHashArray = () => {
    const hash = window.location.hash;
    if (hash) {
        return hash.split('/').filter((item) => {
            return item.indexOf('?') !== 0 && item.length !== 1 && item.length !== 0;
        }).map((item) => {
            if (item.charAt(0) === '#') {
                return item.substr(1);
            }
            else {
                return item;
            }
        });
    }
    else {
        return false;
    }
};
export const swingTo = (pos) => {
    pos /= 0.5;
    if (pos < 1) {
        return 0.5 * Math.pow(pos, 2);
    }
    return -0.5 * ((pos -= 2) * pos - 2);
};
export const doScrolling = (scrollTargetY, speed, parent = window, callback) => {
    // cancel previous scrolling if new scrolling is triggered
    window.cancelAnimationFrame(scrollAnimationFrame);
    const scrollY = parent instanceof HTMLElement ? parent.scrollTop : parent.pageYOffset;
    let currentTime = 0;
    // min time .1, max time .8 seconds
    const time = Math.max(.1, Math.min(Math.abs(scrollY - scrollTargetY) / speed, .8));
    /**
     * Tick a scroll point
     */
    function tick() {
        currentTime += 1 / 60;
        const p = currentTime / time;
        const t = swingTo(p);
        if (p < 1) {
            scrollAnimationFrame = window.requestAnimationFrame(tick);
            parent.scrollTo(0, scrollY + ((scrollTargetY - scrollY) * t));
        }
        else {
            parent.scrollTo(0, scrollTargetY);
            if (callback) {
                callback();
            }
        }
    }
    tick();
};
export const scrollToElement = (element, speed, offset, focusScrolledElement) => {
    if (typeof element === 'string') {
        element = document.getElementById(element);
    }
    if (element) {
        const duration = speed || 500;
        const offsetY = element.getBoundingClientRect().top + window.pageYOffset;
        const topOffsetY = offset || getDefaultPageTopOffset(offsetY);
        const combinedOffsetY = Math.max(offsetY - topOffsetY, 0);
        const callback = focusScrolledElement ? () => {
            if (element && element instanceof HTMLElement) {
                if (!isFocusable(element)) {
                    element.setAttribute('tabindex', '-1');
                }
                element.focus();
            }
        } : undefined;
        doScrolling(combinedOffsetY, duration, window, callback);
    }
};
export const isOnScreen = (element) => {
    if (typeof element === 'string') {
        element = document.getElementById(element);
    }
    if (element) {
        const rect = element.getBoundingClientRect();
        const html = document.documentElement;
        /* istanbul ignore else */
        if (html) {
            return (rect.top >= getDefaultPageTopOffset() &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || html.clientHeight) &&
                rect.right <= (window.innerWidth || html.clientWidth));
        }
    }
    return false;
};
export const isHeaderOpen = (refOffsetY) => {
    const header = document.getElementById('header');
    if (!refOffsetY) {
        return !!header && !header.classList.contains('has-scrolled');
    }
    else {
        const documentHeight = document.documentElement ? document.documentElement.offsetHeight : 0;
        const windowHeight = window.innerHeight;
        const headerHeight = header ? header.clientHeight : 0;
        if (refOffsetY <= headerHeight || windowHeight + refOffsetY >= documentHeight - headerHeight) {
            return true;
        }
        return false;
    }
};
export const getDefaultPageTopOffset = (refOffsetY) => {
    const affixes = document.getElementsByClassName('affix');
    let pageTopOffset = 0;
    for (const affix of affixes) {
        pageTopOffset += affix.clientHeight;
    }
    return isHeaderOpen(refOffsetY) ? pageTopOffset + 150 : pageTopOffset;
};
export const stringToNum = (str) => {
    return Number(str.replace(',', '.'));
};
export const numToString = (num, decimals) => {
    return num.toFixed(decimals).toString().replace('.', ',');
};
export const offset = (element) => {
    const rect = element.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement && document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement && document.documentElement.scrollTop;
    return {
        left: rect.left + (scrollLeft || 0),
        top: rect.top + (scrollTop || 0),
    };
};
export const setDocumentScrollTop = (scrollPosition) => {
    /* istanbul ignore else */
    if (document.documentElement) {
        document.documentElement.scrollTop = document.body.scrollTop = scrollPosition;
    }
};
export const getParameterByName = (name, url = window.location.href) => {
    const regex = new RegExp('[?&]' + name.replace(/[[\]]/g, '\\$&') + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
        return null;
    }
    if (!results[2]) {
        return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};
export const getParentsUntil = (elem, parent) => {
    // Setup parents array
    const parents = [];
    // Get matching parent elements
    for (; elem && elem !== document.documentElement; elem = elem.parentNode) {
        if (parent && elem === parent) {
            break;
        }
        parents.push(elem);
    }
    return parents;
};
export const getOffsetFromParent = (element, parent) => {
    const pos = {
        left: 0,
        top: 0,
    };
    getParentsUntil(element, parent).forEach((parentEl) => {
        /* istanbul ignore else */
        if (parentEl instanceof HTMLElement) {
            const parentElStyle = getComputedStyle(parentEl);
            if (parentElStyle.position === 'static') {
                return;
            }
            pos.top += parentEl.offsetTop;
            pos.left += parentEl.offsetLeft;
        }
    });
    return pos;
};
export const disablePageAccess = () => {
    const contentElement = document.querySelector('#page-container');
    if (contentElement) {
        contentElement.setAttribute('inert', 'inert');
        contentElement.setAttribute('aria-hidden', 'true');
    }
};
export const enablePageAccess = () => {
    const contentElement = document.querySelector('#page-container');
    if (contentElement) {
        contentElement.removeAttribute('inert');
        contentElement.removeAttribute('aria-hidden');
    }
};
export const fontsReady = () => __awaiter(void 0, void 0, void 0, function* () {
    return yield document.fonts.ready;
});
export * from './hooks/use-current-breakpoint';
export * from './hooks/use-is-mounted';
export * from './hooks/use-responsive-props';
export * from './hooks/use-previous';
